import React, { useState } from 'react';
import { FormInputWrapper, Select, DateInput, TextArea, RadioGroup } from 'form/infrastructure/reactHookForm';
import cx from 'classnames';
import { useFormContext } from 'react-hook-form';
import css from './Vulnerabilities.module.scss';
import { Modal, Button } from 'lmc-ui-library';

import {
  isInformationProvidedByCustomer as isVulnerabilityInformationProvidedByCustomer,
  InformationProvidedBy,
  InformationSource,
  VulnerabilityField,
} from 'modules/cm/Vulnerabilities/Vulnerability';
import { CaseDetails } from '../../../CaseDetailsPage.types';
import { Form } from './Vulnerability';

interface Props {
  categoryResources: CaseDetails['resources']['vulnerabilityCategories'];
  informationResources: CaseDetails['resources']['informationProvidedBy'];
}

const VulnerabilityForm = ({ categoryResources, informationResources }: Props) => {
  const [categoryOptions, setCategoryOptions] = useState(categoryResources);
  const [informationProvidedByOptions, setInformationProvidedByOptions] = useState(informationResources);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false);

  const { setValue, watch, clearErrors } = useFormContext<Form>();

  const watched: Partial<Form> = {
    [VulnerabilityField.InformationProvidedBy]: watch(VulnerabilityField.InformationProvidedBy),
  };

  const searchCategory = (value: string) => {
    if (!value.trim()) {
      setCategoryOptions(categoryResources);
    } else {
      const filtered = categoryResources.filter((category) =>
        category.option.toLowerCase().includes(value.toLowerCase()),
      );
      setCategoryOptions(filtered);
    }
  };

  const searchInformation = (value: string) => {
    if (!value.trim()) {
      setInformationProvidedByOptions(informationResources);
    } else {
      const filtered = informationResources.filter((info) => info.option.toLowerCase().includes(value.toLowerCase()));
      setInformationProvidedByOptions(filtered);
    }
  };
  const openHelpTextModal = () => {
    setIsHelpModalOpen(true);
  };
  const closeHelpTextModal = () => {
    setIsHelpModalOpen(false);
  };

  const areConsentsDisabled = (value?: InformationSource) => {
    return !isVulnerabilityInformationProvidedByCustomer(
      value || (watched[VulnerabilityField.InformationProvidedBy] as InformationSource),
    );
  };

  return (
    <div className={css.formWrapper}>
      <FormInputWrapper title={'Please select the vulnerability category'} name={VulnerabilityField.Category}>
        <Select
          options={categoryOptions.map((r) => ({
            label: r.option.toString(),
            name: r.value.toString(),
            selected: false,
          }))}
          onOptionClick={(option) => {
            setValue(VulnerabilityField.Category, option.name, { shouldValidate: true });
          }}
          onSearchInputChange={searchCategory}
        />
      </FormInputWrapper>

      <FormInputWrapper
        title={'Comments and additional information'}
        name={VulnerabilityField.CommentsAndAdditionalInformation}>
        <TextArea rows={4} />
      </FormInputWrapper>

      <FormInputWrapper title={'Information provided by'} name={VulnerabilityField.InformationProvidedBy}>
        <Select
          options={informationProvidedByOptions.map((r) => ({
            label: r.option.toString(),
            name: r.value.toString(),
            selected: false,
          }))}
          onOptionClick={(option) => {
            const value = option.name as InformationProvidedBy;
            setValue(VulnerabilityField.InformationProvidedBy, value, {
              shouldValidate: true,
            });

            if (areConsentsDisabled(value)) {
              setValue(VulnerabilityField.IsAppropriateForExplicitConsent, null);
              setValue(VulnerabilityField.IsExplicitConsentFromCustomer, null);
              setValue(VulnerabilityField.DateOfConsent, null);

              clearErrors([
                VulnerabilityField.IsAppropriateForExplicitConsent,
                VulnerabilityField.IsExplicitConsentFromCustomer,
                VulnerabilityField.DateOfConsent,
              ]);
            }
          }}
          onSearchInputChange={searchInformation}
        />
      </FormInputWrapper>

      <div
        className={cx({
          [css.wrapperDisabled]: areConsentsDisabled(),
        })}>
        <FormInputWrapper
          title={'Is it appropriate to ask for explicit consent?'}
          name={VulnerabilityField.IsAppropriateForExplicitConsent}
          help={{ onClick: openHelpTextModal, text: 'Help' }}>
          <RadioGroup
            disabled={areConsentsDisabled()}
            isDisplayFlex={true}
            name={VulnerabilityField.IsAppropriateForExplicitConsent}
            options={[
              {
                value: 'true',
                text: <>Yes</>,
              },
              {
                value: 'false',
                text: <>No</>,
              },
            ]}
            transform={{
              input: (value) => {
                return value !== null ? value.toString() : null;
              },
              output: (value) => {
                return value !== null ? value === 'true' : null;
              },
            }}
          />
        </FormInputWrapper>

        <FormInputWrapper
          title={'Is explicit consent given by the customer?'}
          name={VulnerabilityField.IsExplicitConsentFromCustomer}>
          <RadioGroup
            disabled={areConsentsDisabled()}
            name={VulnerabilityField.IsExplicitConsentFromCustomer}
            isDisplayFlex={true}
            options={[
              {
                value: 'true',
                text: <>Yes</>,
              },
              {
                value: 'false',
                text: <>No</>,
              },
            ]}
            transform={{
              input: (value) => {
                return value !== null ? value.toString() : null;
              },
              output: (value) => {
                return value !== null ? value === 'true' : null;
              },
            }}
          />
        </FormInputWrapper>

        <FormInputWrapper title={'Date of consent'} name={VulnerabilityField.DateOfConsent}>
          <DateInput />
        </FormInputWrapper>
      </div>

      <Modal showCloseBtn={false} isOpen={isHelpModalOpen} onClose={closeHelpTextModal} className={css.modal}>
        <Modal.Content>
          <div className={css.modalText}>
            <div className={css.modalTitle}>Explicit consent</div>
            If the vulnerability relates to Health or Gender Transitioning (Category) you should consider whether it is
            appropriate to ask for Explicit consent. You may consider it inappropriate if:
            <ol>
              <li>Emotionally distressed </li>
              <li>Divulge a life-threatening illness </li>
              <li>Low mental capacity or cognitive disabilities </li>
              <li>Poor mental health</li>
            </ol>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button mode="ghost" borderVariant="rectangle" onClick={closeHelpTextModal} width={155}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VulnerabilityForm;
